import Intercom, {
  hide,
  onUnreadCountChange,
  show,
  showConversation,
  showMessages,
  showNewMessage,
} from '@intercom/messenger-js-sdk'
import capitalize from 'lodash/capitalize'
import { createContext, ReactElement, ReactNode, useContext, useEffect, useState } from 'react'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { UserOrganizationMembership } from '@modules-deprecated/app/organization/types/userOrganizationMembership'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { queryClient } from '../config/queryClient'
import { QueryKeys } from '../enums/queryKeys'
import { localeToLanguage } from '../utils/localeToLanguage'

function userRole(membership: UserOrganizationMembership) {
  switch (`${membership?.via}:${membership?.level}`) {
    case 'direct:owner':
      return 'business_owner'
    case 'direct:member':
      return 'business_employee'
    case 'umbrella:owner':
      return 'accountant_admin'
    case 'umbrella:member':
      return 'accountant_employee'
  }
}

export type IntercomContextState = {
  hide: () => void
  show: () => void
  showConversation: (conversationId: string) => void
  showMessages: () => void
  showNewMessage: (prePopulatedContent: string) => void
  unreadConversationsCount: number
}

const initialState: IntercomContextState = {
  hide,
  show,
  showConversation,
  showMessages,
  showNewMessage,
  unreadConversationsCount: 0,
}

export const IntercomContext = createContext<IntercomContextState>(initialState)

interface IntercomContextProviderProps {
  children: ReactNode
}

export const IntercomContextProvider = ({ children }: IntercomContextProviderProps): ReactElement => {
  const [unreadConversationsCount, setUnreadConversationsCount] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)
  const { user, intercomGlobalIdUserHash } = useCurrentUser()
  const { organization } = useUserOrganization()
  const membership = queryClient.getQueryData<UserOrganizationMembership>(
    [QueryKeys.UserOrganizationMembership, organization?.id],
    { inactive: true },
  )

  useEffect(() => {
    const appId = window.ENV?.INTERCOM_APP_ID
    if (!appId || !user?.globalId || !intercomGlobalIdUserHash) {
      return
    }

    const capitalizedName = user.name.split(' ').map(capitalize).join(' ')

    /* eslint-disable @typescript-eslint/naming-convention */
    Intercom({
      app_id: appId,
      region: 'eu',
      user_id: user.globalId,
      user_hash: intercomGlobalIdUserHash,
      language_override: localeToLanguage[user.locale],
      name: capitalizedName,
      created_at: new Date(user.createdTime).getTime() / 1000,
      email: user.email,
      phone: user.phone,
      legacy_user_id: user.id,
      hide_default_launcher: true,
      user_role: membership ? userRole(membership) : undefined,
      company: organization
        ? {
            company_id: organization.globalId,
            name: organization.name,
            created_at: organization.createdTime,
            plan: organization.subscriptionProductPlan,
            vat_number: organization.vatNo,
            legal_registration_number: organization.registrationNo,
            accounting_firm_id: organization?.ownerUmbrellaId,
            country: organization.countryId,
            brand: organization.brand,
            origin_product: organization.isCegid ? 'cegid' : 'shine',
            migration_origin: organization.migrationOrigin ?? undefined,
            random_bucket: organization.randomBucket,
            is_test: organization.isTest,
            ui_setup: organization.uiSetup,
            legacy_company_id: organization.id,
            subscription_payer: organization.consolidatedBilling ? 'accountant' : 'business',
          }
        : undefined,
    })
    /* eslint-enable @typescript-eslint/naming-convention */
    setIsInitialized(true)
  }, [membership, organization, user, intercomGlobalIdUserHash])

  useEffect(() => {
    if (isInitialized) {
      onUnreadCountChange((count: number) => {
        setUnreadConversationsCount(count)
      })
    }
  }, [isInitialized])

  const value: IntercomContextState = {
    hide,
    show,
    showConversation,
    showMessages,
    showNewMessage,
    unreadConversationsCount,
  }

  return <IntercomContext.Provider value={value}>{children}</IntercomContext.Provider>
}

export const useIntercom = () => {
  const context = useContext(IntercomContext)

  if (!context) {
    throw new Error('IntercomContextProvider is missing in the module!')
  }

  return context
}
